import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { billingAPI } from '../services/api';
import { useTranslation } from '../context/LanguageContext';
import nativePurchaseService from '../services/nativePurchaseService';
import './Pricing.css';

export default function Pricing() {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [searchParams] = useSearchParams();
  const [interval, setInterval] = useState('annual');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [isNative, setIsNative] = useState(false);
  const [nativeReady, setNativeReady] = useState(false);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    loadSubscription();
    initNativePurchases();
  }, []);

  const initNativePurchases = async () => {
    if (nativePurchaseService.isNative()) {
      setIsNative(true);
      try {
        await nativePurchaseService.initialize();
        setNativeReady(nativePurchaseService.initialized);
      } catch (err) {
        console.warn('IAP init failed, retrying in 3s...', err);
        setTimeout(async () => {
          try {
            await nativePurchaseService.initialize();
            setNativeReady(nativePurchaseService.initialized);
          } catch (retryErr) {
            console.warn('IAP init retry failed:', retryErr);
            setNativeReady(false);
          }
        }, 3000);
      }
    }
  };

  const loadSubscription = async () => {
    try {
      const res = await billingAPI.getSubscription();
      setSubscription(res.data);
    } catch (error) {
      console.error('Load subscription error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (tier) => {
    // Route to native purchase on mobile
    if (isNative) {
      return handleNativePurchase(tier);
    }

    setCheckoutLoading(tier);
    try {
      const res = await billingAPI.createCheckout(tier, interval);
      window.location.href = res.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      alert(error.response?.data?.error || 'Failed to start checkout');
    } finally {
      setCheckoutLoading(null);
    }
  };

  // Native in-app purchase (iOS/Android)
  const handleNativePurchase = async (tier) => {
    if (!nativeReady) {
      // Try to initialize one more time before giving up
      setCheckoutLoading(tier);
      try {
        await nativePurchaseService.initialize();
        setNativeReady(nativePurchaseService.initialized);
        if (!nativePurchaseService.initialized) {
          setCheckoutLoading(null);
          alert('Subscriptions are not available yet. Please try again in a moment.');
          return;
        }
      } catch (err) {
        setCheckoutLoading(null);
        alert('Subscriptions are not available yet. Please try again in a moment.');
        return;
      }
    }

    setCheckoutLoading(tier);
    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Trigger native purchase flow
      const result = await nativePurchaseService.purchase(tier, currentUser._id);

      // Verify purchase on backend
      const verifyRes = await billingAPI.verifyMobile({
        transactionId: result.transactionId,
        productId: result.productId,
        purchaseToken: result.purchaseToken,
        receipt: result.receipt,
        platform: result.platform,
        tier: result.tier,
      });

      if (verifyRes.data.success) {
        await loadSubscription();
        alert('🎉 ' + verifyRes.data.message);
      } else {
        alert('Purchase completed but verification failed. Please contact support.');
      }
    } catch (error) {
      if (error.message === 'CANCELLED') return;
      console.error('Native purchase error:', error);
      // Don't show raw error messages to users
      if (error.message?.includes('not available yet')) {
        alert('Subscriptions are loading. Please wait a moment and try again.');
      } else if (error.message?.includes('Store not available')) {
        alert('In-app purchases are not supported on this device.');
      } else {
        alert('Purchase could not be completed. Please try again.');
      }
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleManageBilling = async () => {
    if (isNative) {
      try {
        await nativePurchaseService.manageSubscriptions();
      } catch (error) {
        console.error('Manage subscriptions error:', error);
      }
      return;
    }

    try {
      const res = await billingAPI.createPortal();
      window.location.href = res.data.url;
    } catch (error) {
      console.error('Portal error:', error);
      alert('Failed to open billing portal');
    }
  };

  // Restore purchases (native only)
  const handleRestore = async () => {
    if (!nativeReady) return;
    
    setRestoring(true);
    try {
      const purchases = await nativePurchaseService.restorePurchases();
      
      if (purchases && purchases.length > 0) {
        const res = await billingAPI.restoreMobile({
          purchases,
          platform: nativePurchaseService.getPlatform(),
        });

        if (res.data.success) {
          await loadSubscription();
          alert('✅ ' + res.data.message);
        } else {
          alert('No active subscriptions found to restore.');
        }
      } else {
        alert('No previous purchases found.');
      }
    } catch (error) {
      console.error('Restore error:', error);
      alert('Failed to restore purchases. Please try again.');
    } finally {
      setRestoring(false);
    }
  };

  const currentTier = subscription?.tier || 'free';

  const plans = [
    {
      id: 'free',
      name: t('planFree'),
      emoji: '🌱',
      tagline: t('planFreeTagline'),
      monthlyPrice: 0,
      annualPrice: 0,
      features: [
        t('planFreeF1'),
        t('planFreeF2'),
        t('planFreeF3'),
        t('planFreeF4'),
        t('planFreeF5'),
      ],
      limitations: [
        t('planFreeLim1'),
        t('planFreeLim2'),
      ]
    },
    {
      id: 'legacy_keeper',
      name: 'Legacy Keeper',
      emoji: '🌳',
      tagline: t('planKeeperTagline'),
      monthlyPrice: 10,
      annualPrice: 96,
      popular: true,
      features: [
        t('planKeeperF1'),
        t('planKeeperF2'),
        t('planKeeperF3'),
        t('planKeeperF4'),
        t('planKeeperF5'),
        t('planKeeperF6'),
        t('planKeeperF7'),
      ],
      limitations: []
    },
    {
      id: 'legacy_vault',
      name: 'Legacy Vault',
      emoji: '🏛️',
      tagline: t('planVaultTagline'),
      monthlyPrice: 30,
      annualPrice: 288,
      features: [
        t('planVaultF1'),
        t('planVaultF2'),
        t('planVaultF3'),
        t('planVaultF4'),
        t('planVaultF5'),
        t('planVaultF6'),
        t('planVaultF7'),
        t('planVaultF8'),
      ],
      limitations: []
    }
  ];

  const billingSuccess = searchParams.get('billing') === 'success';

  return (
    <div className="pricing-page">
      <button className="btn-back" onClick={() => navigate('/settings')}>
        ← {t('pricingBackToSettings')}
      </button>

      {billingSuccess && (
        <div className="billing-success-banner">
          <span>🎉</span> {t('pricingWelcome')}
        </div>
      )}

      <div className="pricing-header">
        <h1>{t('pricingTitle')}</h1>
        <p className="pricing-subtitle">
          {t('pricingSubtitle')}
        </p>

        {!isNative && (
        <div className="interval-toggle">
          <button
            className={`interval-btn ${interval === 'monthly' ? 'active' : ''}`}
            onClick={() => setInterval('monthly')}
          >
            {t('pricingMonthly')}
          </button>
          <button
            className={`interval-btn ${interval === 'annual' ? 'active' : ''}`}
            onClick={() => setInterval('annual')}
          >
            {t('pricingAnnual')}
            <span className="save-badge">{t('pricingSave20')}</span>
          </button>
        </div>
        )}

        {isNative && (
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginTop: '8px' }}>
            Monthly subscriptions · Cancel anytime
          </p>
        )}
      </div>

      <div className="plans-grid">
        {plans.map((plan) => {
          const isCurrent = currentTier === plan.id;
          const nativePrice = isNative ? nativePurchaseService.getPriceString(plan.id) : null;
          const price = isNative ? plan.monthlyPrice : (interval === 'annual' ? plan.annualPrice : plan.monthlyPrice);
          const monthlyEquiv = isNative ? plan.monthlyPrice : (interval === 'annual' ? (plan.annualPrice / 12).toFixed(0) : plan.monthlyPrice);

          return (
            <div 
              key={plan.id} 
              className={`plan-card ${plan.popular ? 'popular' : ''} ${isCurrent ? 'current' : ''}`}
            >
              {plan.popular && <div className="popular-badge">{t('pricingMostPopular')}</div>}
              {isCurrent && <div className="current-badge">{t('pricingCurrentPlan')}</div>}

              <div className="plan-emoji">{plan.emoji}</div>
              <h2 className="plan-name">{plan.name}</h2>
              <p className="plan-tagline">{plan.tagline}</p>

              <div className="plan-price">
                {price === 0 ? (
                  <span className="price-amount">{t('planFree')}</span>
                ) : isNative && nativePrice ? (
                  <>
                    <span className="price-amount">{nativePrice}</span>
                    <span className="price-period">/{t('pricingMo')}</span>
                  </>
                ) : (
                  <>
                    <span className="price-currency">$</span>
                    <span className="price-amount">{monthlyEquiv}</span>
                    <span className="price-period">/{t('pricingMo')}</span>
                    {!isNative && interval === 'annual' && (
                      <div className="price-billed">
                        {t('pricingBilled')} ${price}/{t('pricingYear')}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="plan-features">
                {plan.features.map((feature, i) => (
                  <div key={i} className="feature-item included">
                    <span className="feature-icon">✓</span>
                    {feature}
                  </div>
                ))}
                {plan.limitations.map((limitation, i) => (
                  <div key={`lim-${i}`} className="feature-item excluded">
                    <span className="feature-icon">✕</span>
                    {limitation}
                  </div>
                ))}
              </div>

              <div className="plan-action">
                {isCurrent ? (
                  (subscription?.stripeCustomerId || subscription?.mobileTransactionId) ? (
                    <button className="btn-manage" onClick={handleManageBilling}>
                      {isNative ? 'Manage Subscription' : t('pricingManage')}
                    </button>
                  ) : (
                    <button className="btn-current" disabled>
                      {t('pricingCurrentPlan')}
                    </button>
                  )
                ) : plan.id === 'free' ? (
                  currentTier !== 'free' ? (
                    <button className="btn-manage" onClick={handleManageBilling}>
                      {t('pricingDowngrade')}
                    </button>
                  ) : (
                    <button className="btn-current" disabled>
                      {t('pricingCurrentPlan')}
                    </button>
                  )
                ) : (() => {
                  const tierRank = { free: 0, legacy_keeper: 1, legacy_vault: 2 };
                  const isDowngrade = tierRank[plan.id] < tierRank[currentTier];
                  if (isDowngrade) {
                    return (
                      <button className="btn-manage" onClick={handleManageBilling}>
                        {t('pricingDowngrade')}
                      </button>
                    );
                  }
                  return (
                    <button
                      className={`btn-upgrade ${plan.popular ? 'btn-primary' : ''}`}
                      onClick={() => handleCheckout(plan.id)}
                      disabled={checkoutLoading === plan.id}
                    >
                      {checkoutLoading === plan.id ? t('pricingRedirecting') :
                       currentTier === 'free' ? t('pricingGetStarted') : t('pricingUpgrade')}
                    </button>
                  );
                })()}
              </div>

              {subscription?.cancelAtPeriodEnd && isCurrent && (
                <div className="cancel-notice">
                  {t('pricingCancelsAt')} {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {subscription && subscription.tier !== 'free' && (
        <div className="usage-section">
          <h3>{t('pricingYourUsage')}</h3>
          <div className="usage-grid">
            <div className="usage-item">
              <div className="usage-label">{t('stories')}</div>
              <div className="usage-value">
                {subscription.usage?.stories || 0}
                {subscription.limits?.maxStories > 0 && (
                  <span className="usage-limit"> / {subscription.limits.maxStories}</span>
                )}
                {subscription.limits?.maxStories === -1 && (
                  <span className="usage-limit"> / ∞</span>
                )}
              </div>
            </div>
            <div className="usage-item">
              <div className="usage-label">{t('pricingFamilyCircles')}</div>
              <div className="usage-value">
                {subscription.usage?.familyCircles || 0}
                {subscription.limits?.maxFamilyCircles > 0 && (
                  <span className="usage-limit"> / {subscription.limits.maxFamilyCircles}</span>
                )}
                {subscription.limits?.maxFamilyCircles === -1 && (
                  <span className="usage-limit"> / ∞</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

<div style={{
  textAlign: 'center', marginTop: '24px', padding: '14px',
  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: '12px'
}}>
<span style={{ fontSize: '16px', marginRight: '4px', filter: 'none', opacity: 1 }}>🎁</span>
<span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
  Have a gift code?{' '}
  <span onClick={() => navigate('/redeem')} style={{ color: '#a78bfa', cursor: 'pointer', fontWeight: '500' }}>
    Redeem here
  </span>
</span>
</div>

      <div className="pricing-faq">
        <h3>{t('pricingQuestions')}</h3>
        <div className="faq-item">
          <strong>{t('pricingFaq1Q')}</strong>
          <p>{t('pricingFaq1A')}</p>
        </div>
        <div className="faq-item">
          <strong>{t('pricingFaq2Q')}</strong>
          <p>{t('pricingFaq2A')}</p>
        </div>
        <div className="faq-item">
          <strong>{t('pricingFaq3Q')}</strong>
          <p>{t('pricingFaq3A')}</p>
        </div>
        {isNative && (
          <div className="faq-item">
            <strong>How do I cancel my subscription?</strong>
            <p>
              You can manage or cancel your subscription anytime through your {
                nativePurchaseService.getPlatform() === 'android' 
                  ? 'Google Play Store → Subscriptions' 
                  : 'Settings → Apple ID → Subscriptions'
              }.
            </p>
          </div>
        )}
      </div>

      {/* Restore purchases (native only) */}
      {isNative && (
        <div style={{ textAlign: 'center', margin: '24px 0' }}>
          <button 
            onClick={handleRestore}
            disabled={restoring}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.15)',
              color: 'rgba(255,255,255,0.6)',
              padding: '10px 24px',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: restoring ? 'not-allowed' : 'pointer',
            }}
          >
            {restoring ? 'Restoring...' : '🔄 Restore Purchases'}
          </button>
          <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '12px', marginTop: '8px' }}>
            Already subscribed? Tap to restore your purchase.
          </p>

          {/* Apple-required subscription disclosure */}
          <div style={{
            marginTop: '24px',
            padding: '16px',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '10px',
            textAlign: 'left',
            maxWidth: '480px',
            margin: '24px auto 0',
          }}>
            <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', lineHeight: '1.6', margin: 0 }}>
              <strong style={{ color: 'rgba(255,255,255,0.55)' }}>Subscription Information</strong><br />
              Payment will be charged to your Apple ID account at confirmation of purchase.
              Subscriptions automatically renew unless cancelled at least 24 hours before the end of the current period.
              Your account will be charged for renewal within 24 hours prior to the end of the current period.
              Manage or cancel your subscription anytime in your{' '}
              {nativePurchaseService.getPlatform() === 'android' ? 'Google Play account settings' : 'Apple ID account settings'}.{' '}
              <a href="https://echomemoir.ai/privacy" target="_blank" rel="noopener noreferrer"
                style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'underline' }}>
                Privacy Policy
              </a>{' '}·{' '}
              <a href="https://echomemoir.ai/terms" target="_blank" rel="noopener noreferrer"
                style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'underline' }}>
                Terms of Use
              </a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}